import axios from "axios";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import { useToast } from "../../../components/Toast";
import { Loading } from "../../../components/Loading";
import TripExpenseSummary from "../../../components/TripExpenseSummary";
import ExpenseTab, {
  ExpenseItem,
  Payment,
} from "../../../components/ExpenseTab";
import {
  Box,
  Breadcrumbs,
  Button,
  Step,
  Stepper,
  Link,
  Paper,
  StepLabel,
  Typography,
  Container,
  TextField,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import BasicVehicleDetailsView from "../../../components/BasicVehicleDetailsView";
import { fetchVehicleDetails } from "./AddBasicTripDetails";
import { getValidToken } from "../../../utils/authUtils";
import { commonTextFieldProps } from "../../../utils/styles";

const fetchExpenseDetails = async ({
  tripId,
  setIsLoading,
  copyExpense = false,
}: any) => {
  try {
    setIsLoading(true);
    const token = await getValidToken();
    const url = copyExpense
      ? `${process.env.REACT_APP_API_HOST}trips/${tripId}/copy-expenses`
      : `${process.env.REACT_APP_API_HOST}trips/${tripId}/expenses`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response?.data?.isSuccess) {
      return response?.data?.data || null;
    } else {
      throw new Error("Failed to fetch expense details");
    }
  } catch (error) {
    console.error("Error fetching expense details:", error);
    return [];
  }
};

const updateTrip = async ({ tripId, data }: any) => {
  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}/advance-payment`;
    const response = await axios.patch(url, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response?.data?.isSuccess) {
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error updating trip:", error);
    return false;
  }
};

const createTripExpense = async ({ tripId, expenseData }: any) => {
  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}/expenses`;

    const dataWithoutId = expenseData?.map((expense: any) => {
      const { id, ...expenseWithoutId } = expense;
      return expenseWithoutId;
    });

    const response = await axios.post(url, dataWithoutId, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response?.data?.isSuccess) {
      return true;
    }
    return false;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      console.error("Session expired. Please login again.", "error");
    } else {
      console.error("Error adding trip expense:", error);
    }
    return false;
  }
};

const deleteTripExpense = async ({ tripId, expenseData }: any) => {
  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}/log-expenses-deletion`;

    const response = await axios.post(url, expenseData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response?.data?.isSuccess) {
      return true;
    }
    return false;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      console.error("Session expired. Please login again.", "error");
    } else {
      console.error("Error adding trip expense:", error);
    }
    return false;
  }
};

const updateTripExpense = async ({ tripId, expenseData }: any) => {
  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}/expenses`;

    const response = await axios.put(url, expenseData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response?.data?.isSuccess) {
      return true;
    }
    return false;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      console.error("Session expired. Please login again.", "error");
    } else {
      console.error("Error updating trip expense:", error);
    }
    return false;
  }
};

const AddExpenseDetails = () => {
  const { vehicleId, tripId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useFirebaseAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get("edit") === "true";
  const isCopyMode = searchParams.get("copy") === "true";
  const newTripId = searchParams.get("newTripId");
  const [expenseDetails, setExpenseDetails] = useState<any>(null);
  const [expenses, setExpenses] = useState<any>([]);
  const [fetchedExpenses, setFetchedExpenses] = useState<any>([]);
  const [deletedExpenses, setDeletedExpenses] = useState<any>([]);
  const [expenseSummary, setExpenseSummary] = useState<any>(null);
  const [hasDataChanged, setHasDataChanged] = useState(false);
  const currentStep = 2;
  const steps = ["Basic Details", "Income Details", "Expense Details"];
  const handleStepClick = async (stepIndex: number) => {
    if (isEditMode) {
      if (stepIndex === 0) {
        navigate(`/trip/basic/${vehicleId}/${tripId}?edit=${isEditMode}`);
      } else if (stepIndex === 1) {
        navigate(`/trip/income/${vehicleId}/${tripId}?edit=${isEditMode}`);
      }
    } else if (isCopyMode) {
      if (stepIndex === 0) {
        navigate(
          `/trip/basic/${vehicleId}?copy=${isCopyMode}&newTripId=${newTripId}`
        );
      } else if (stepIndex === 1) {
        navigate(
          `/trip/income/${vehicleId}/${tripId}?copy=${isCopyMode}&newTripId=${newTripId}`
        );
      }
    } else {
      if (stepIndex === 0) {
        navigate(`/trip/basic/${vehicleId}/${tripId}`);
      } else if (stepIndex === 1) {
        navigate(`/trip/income/${vehicleId}/${tripId}`);
      }
    }
  };
  const [vehicleDetails, setVehicleDetails] = useState<any | null>(null);
  const [advancePaymentChanged, setAdvancePaymentChanged] = useState(false);

  useEffect(() => {
    if (user && tripId && !authLoading) {
      fetchVehicleDetails({
        showToast,
        navigate,
        setIsLoading,
        setVehicleDetails,
        vehicleId: vehicleId || "",
      });
      if (isEditMode || isCopyMode) {
        fetchExpenseDetails({
          tripId,
          setIsLoading,
          copyExpense: isCopyMode,
        })
          .then((res) => {
            setExpenses(res?.expenses || []);
            setFetchedExpenses(res?.expenses || []);
            setDeletedExpenses(res?.deletedExpenses || []);
            setExpenseSummary({
              totalExpense: res?.summary?.totalExpense,
              paidExpenses: res?.summary?.totalPaid,
              totalBalance: res?.summary?.totalBalance,
              cashPaymentsCount: res?.summary?.cashPaymentsCount,
              onlinePaymentsCount: res?.summary?.onlinePaymentsCount,
            });
            setExpenseDetails({
              ...expenseDetails,
              advancePayment: res?.advancePaymentDetails?.advancePayment,
              advancePaymentPaidTo:
                res?.advancePaymentDetails?.advancePaymentPaidTo,
              advancePaymentLocation:
                res?.advancePaymentDetails?.advancePaymentLocation,
            });
          })
          .finally(() => setIsLoading(false));
      }
    }
  }, [user, tripId, authLoading]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleExpensesUpdate = (updatedExpenses: ExpenseItem[]) => {
    setExpenses(updatedExpenses);
    setHasDataChanged(true);
  };

  const handleTripExpenseCreation = async () => {
    try {
      setIsLoading(true);
      // Update advance payment details
      if (Number(expenseDetails?.advancePayment) > 0 && advancePaymentChanged) {
        const updateSuccess = await updateTrip({
          tripId: isCopyMode ? newTripId : tripId,
          data: {
            advancePayment: expenseDetails?.advancePayment,
            advancePaymentPaidTo: expenseDetails?.advancePaymentPaidTo,
            advancePaymentLocation: expenseDetails?.advancePaymentLocation,
          },
        });
        if (!updateSuccess) {
          showToast("Failed to update advance payment details", "error");
        }
      }

      // Only proceed if expense details have changed
      if (hasDataChanged) {
        // Split expenses into new and existing based on id
        const newExpenses = expenses?.filter(
          (expense: ExpenseItem) => !expense.id
        );
        let isSuccess = true;
        // Create new expenses if any exist
        if (newExpenses.length > 0) {
          const createSuccess = await createTripExpense({
            tripId: isCopyMode ? newTripId : tripId,
            expenseData: newExpenses,
          });
          if (!createSuccess) {
            showToast("Failed to create new expense records", "error");
            isSuccess = false;
          }
        }

        // Filter expenses that need to be renewed
        const expensesToRenew = expenses.filter(
          (expense: ExpenseItem) => expense.renew && expense.id
        );

        // If any such expenses exist
        if (expensesToRenew.length > 0) {
          const updateSuccess = await updateTripExpense({
            tripId: isCopyMode ? newTripId : tripId,
            expenseData: expensesToRenew,
          });
          if (!updateSuccess) {
            showToast("Failed to update selected expense records", "error");
            isSuccess = false;
          }
        }

        //delete edited expense but send the fetched expenses
        const expensesToDelete = expenses.filter(
          (expense: ExpenseItem) => expense?.isEdit
        );
        const expensesToDeleteWithFetchedExpenses = expensesToDelete.map(
          (expense: ExpenseItem) => {
            const fetchedExpense = fetchedExpenses.find(
              (item: ExpenseItem) => item?.id === expense?.id
            );
            return fetchedExpense;
          }
        );
        if (expensesToDelete?.length > 0 && !isCopyMode) {
          const deletedSuccess = await deleteTripExpense({
            tripId: isCopyMode ? newTripId : tripId,
            expenseData: expensesToDeleteWithFetchedExpenses,
          });
          if (!deletedSuccess) {
            showToast("Failed to update selected expense records", "error");
            isSuccess = false;
          }
        }

        if (isSuccess) {
          if (isEditMode) {
            showToast("Trip updated successfully with all details", "success");
          } else {
            showToast("Trip created successfully with all details", "success");
          }
        }
        setHasDataChanged(false);
      } else {
        showToast("No changes to save", "info");
      }

      navigate(`/trip/proof/${tripId}`);
    } catch (error) {
      console.error("Error in trip expense creation flow:", error);
      showToast("Failed to save expenses", "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || authLoading) {
    return <Loading message="Loading..." fullScreen={true} />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            onClick={() => navigate("/dashboard")}
            sx={{ cursor: "pointer" }}
          >
            Dashboard
          </Link>
          {isEditMode ? (
            <Link
              color="inherit"
              onClick={() => navigate("/trips")}
              sx={{ cursor: "pointer" }}
            >
              Trips Management
            </Link>
          ) : (
            <Link
              color="inherit"
              onClick={() => navigate("/trip/select-vehicle")}
              sx={{ cursor: "pointer" }}
            >
              Select Vehicle
            </Link>
          )}
          <Typography color="text.primary">Trip Details</Typography>
        </Breadcrumbs>
      </Box>

      {/* Progress Stepper */}
      <Box sx={{ width: "100%", mb: 4 }}>
        <Stepper activeStep={currentStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                onClick={() => handleStepClick(index)}
                sx={{
                  cursor: isEditMode
                    ? "pointer"
                    : index <= currentStep
                    ? "pointer"
                    : "default",
                  "&:hover": {
                    color: isEditMode
                      ? "primary.main"
                      : index <= currentStep
                      ? "primary.main"
                      : "inherit",
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Vehicle Details */}
      <BasicVehicleDetailsView vehicle={vehicleDetails} />

      {/* <TripExpenseSummary expenseSummary={expenseSummary} /> */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Advance cash on Vehicle</Typography>
          <TextField
            label="Advance Amount"
            type="number"
            value={expenseDetails?.advancePayment}
            onChange={(e) => {
              setAdvancePaymentChanged(true);
              setExpenseDetails({
                ...expenseDetails,
                advancePayment: Number(e.target.value),
              });
            }}
            InputProps={{
              startAdornment: <span style={{ marginRight: 8 }}>₹</span>,
            }}
            {...commonTextFieldProps}
          />
          <TextField
            label="Paid To"
            type="text"
            required={expenseDetails?.advancePayment > 0}
            value={expenseDetails?.advancePaymentPaidTo}
            onChange={(e) => {
              setAdvancePaymentChanged(true);
              setExpenseDetails({
                ...expenseDetails,
                advancePaymentPaidTo: e.target.value,
              });
            }}
            {...commonTextFieldProps}
          />
          <TextField
            label="Paid Location"
            type="text"
            required={expenseDetails?.advancePayment > 0}
            value={expenseDetails?.advancePaymentLocation}
            onChange={(e) => {
              setAdvancePaymentChanged(true);
              setExpenseDetails({
                ...expenseDetails,
                advancePaymentLocation: e.target.value,
              });
            }}
            {...commonTextFieldProps}
          />
        </Box>
      </Paper>
      {/* {!isLoading && (
        <ExpenseTab
          onExpenseSummaryUpdate={setExpenseSummary}
          expensesList={expenses}
          deletedExpenses={deletedExpenses}
          setExpensesList={setExpenses}
          setHasDataChanged={setHasDataChanged}
          advancePaymentAmount={expenseDetails?.advancePayment}
          expenseSummary={expenseSummary}
        />
      )} */}
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        sx={{ mt: 3, mb: 3 }}
      >
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        <Button variant="contained" onClick={handleTripExpenseCreation}>
          Save Changes
        </Button>
      </Box>
    </Container>
  );
};

export default AddExpenseDetails;
